import React, { useMemo, useState } from 'react';
import {
  Bullseye,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Content,
  ExpandableSection,
  Spinner,
  ToggleGroup,
  ToggleGroupItem,
} from '@patternfly/react-core';
import { TimesIcon } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { useAdminVerdaAvailabilityShape } from '../../../hooks/useAdminVerdaAvailabilityHistory';
import type { VerdaHistoryQuery } from '../../../hooks/useAdminVerdaAvailabilityHistory';
import type { VerdaAvailabilityShape, VerdaHistoryWindow } from '../../../types/models/Verda';
import { formatCurrencyAmount } from '../../../utils/money';
import { VerdaErrorAlert } from './VerdaTabParts';
import {
  MARKET_LABELS,
  type VerdaMarket,
  type VerdaSlotTally,
  WEEKDAYS,
  dailyTallies,
  formatShare,
  formatUtc,
  heatFill,
  hourOfWeek,
  neverAvailable,
  shapeLabel,
  slotStart,
  tallyShare,
  timelineBuckets,
} from './verdaAvailabilityHistory';

interface VerdaAvailabilityShapeDetailProps {
  instanceType: string;
  query: VerdaHistoryQuery;
  market: VerdaMarket;
  isStaff: boolean;
  onClose: () => void;
}

const ANYWHERE = 'anywhere';
const SUBTLE = 'pf-v6-u-font-size-sm pf-v6-u-text-color-subtle';

/** "sold out in 12 of 31 samples (38.7%)", or "no sample". */
const tallyText = (tally: VerdaSlotTally): string =>
  tally.sampled === 0
    ? 'no sample'
    : `sold out in ${tally.soldOut} of ${tally.sampled} samples (${formatShare(tally.soldOut / tally.sampled)})`;

/** The scale every cell of this panel is drawn on. */
const ScaleLegend: React.FunctionComponent = () => (
  <div className={`verda-history-legend ${SUBTLE}`} aria-hidden="true">
    <span className="verda-history-legend__item">
      Deployable
      <span className="verda-history-legend__ramp" />
      Sold out
    </span>
    <span className="verda-history-legend__item">
      <span className="verda-history-legend__swatch" style={{ background: heatFill(null) }} />
      No sample
    </span>
    <span className="verda-history-legend__item">
      <span className="verda-history-legend__swatch" style={{ background: 'var(--verda-history-never)' }} />
      Never deployable in the window
    </span>
  </div>
);

/** One strip per series: anywhere first, then each location. */
const Timeline: React.FunctionComponent<{ shape: VerdaAvailabilityShape; market: VerdaMarket }> = ({
  shape,
  market,
}) => {
  const strips = [
    { key: ANYWHERE, label: 'Any location', series: shape.anywhere[market] },
    ...shape.locations.map((l) => ({ key: l.location_code, label: l.location_code, series: l[market] })),
  ];
  const window = shape.window;
  return (
    <div className="verda-history-timeline">
      {strips.map((strip) => {
        const never = neverAvailable(strip.series);
        return (
          <div key={strip.key} className="verda-history-timeline__row">
            <span className="verda-history-timeline__label">
              {strip.label}
              {never && <span className={`${SUBTLE} pf-v6-u-display-block`}>never deployable</span>}
            </span>
            <div className="verda-history-strip" role="img" aria-label={stripSummary(strip.label, strip.series, never)}>
              {timelineBuckets(strip.series).map((bucket) => (
                <span
                  key={bucket.fromSlot}
                  className="verda-history-strip__cell"
                  style={{
                    background:
                      never && bucket.sampled > 0 ? 'var(--verda-history-never)' : heatFill(tallyShare(bucket)),
                  }}
                  title={`${formatUtc(slotStart(window, bucket.fromSlot))} – ${formatUtc(slotStart(window, bucket.toSlot))}: ${tallyText(bucket)}`}
                />
              ))}
            </div>
          </div>
        );
      })}
      <div className="verda-history-timeline__row">
        <span />
        <div className={`verda-history-timeline__axis ${SUBTLE}`}>
          <span>{formatUtc(window.start)}</span>
          <span>{formatUtc(window.end)}</span>
        </div>
      </div>
    </div>
  );
};

const stripSummary = (label: string, series: string, never: boolean): string => {
  if (never) {
    return `${label}: never deployable in this window (not offered there, or sold out throughout)`;
  }
  const tally = timelineBuckets(series, 1)[0] ?? { sampled: 0, soldOut: 0 };
  return `${label}: ${tallyText(tally)}`;
};

/** Weekday × UTC hour grid; a hovered or focused cell's figures show in the readout line above it. */
const Heatmap: React.FunctionComponent<{ series: string; window: VerdaHistoryWindow; caption: string }> = ({
  series,
  window,
  caption,
}) => {
  const grid = useMemo(() => hourOfWeek(series, window), [series, window]);
  const [readout, setReadout] = useState<string | null>(null);
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const pad = (h: number) => String(h).padStart(2, '0');
  return (
    <>
      <p className={SUBTLE} aria-live="polite">
        {readout ?? 'Hover or focus a cell for its figures.'}
      </p>
      <div className="verda-history-heat-scroll">
        <table className="verda-history-heat">
          <caption className="pf-v6-screen-reader">{caption}</caption>
          <thead>
            <tr>
              <th scope="col">
                <span className="pf-v6-screen-reader">Weekday</span>
              </th>
              {hours.map((h) => (
                <th key={h} scope="col" className={SUBTLE}>
                  {h % 3 === 0 ? pad(h) : ''}
                  {h % 3 !== 0 && <span className="pf-v6-screen-reader">{pad(h)}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {WEEKDAYS.map((day, d) => (
              <tr key={day}>
                <th scope="row" className={SUBTLE}>
                  {day}
                </th>
                {hours.map((h) => {
                  const tally = grid[d][h];
                  const text = `${day} ${pad(h)}:00–${pad(h + 1)}:00 UTC: ${tallyText(tally)}`;
                  return (
                    <td key={h}>
                      <span
                        className="verda-history-heat__cell"
                        style={{ background: heatFill(tallyShare(tally)) }}
                        tabIndex={0}
                        role="img"
                        aria-label={text}
                        onMouseEnter={() => setReadout(text)}
                        onFocus={() => setReadout(text)}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

/** One column per UTC day (its sold-out share), with the figures as a table under a toggle. */
const Daily: React.FunctionComponent<{ series: string; window: VerdaHistoryWindow; what: string }> = ({
  series,
  window,
  what,
}) => {
  const days = useMemo(() => dailyTallies(series, window), [series, window]);
  const [showTable, setShowTable] = useState(false);
  const [readout, setReadout] = useState<string | null>(null);
  return (
    <>
      <p className={SUBTLE} aria-live="polite">
        {readout ?? 'Hover or focus a day for its figures.'}
      </p>
      <div className="verda-history-columns">
        {days.map((day) => {
          const share = tallyShare(day);
          const text = `${day.date}: ${tallyText(day)}${day.sampled < day.expected ? `, ${day.expected - day.sampled} slots without a sample` : ''}`;
          return (
            <span
              key={day.date}
              className="verda-history-columns__slot"
              tabIndex={0}
              role="img"
              aria-label={text}
              onMouseEnter={() => setReadout(text)}
              onFocus={() => setReadout(text)}
            >
              {share === null ? (
                <span className="verda-history-columns__empty" style={{ background: heatFill(null) }} />
              ) : (
                <span className="verda-history-columns__bar" style={{ height: `${Math.max(1, share * 100)}%` }} />
              )}
            </span>
          );
        })}
      </div>
      <div className={`verda-history-timeline__axis ${SUBTLE}`}>
        <span>{window.from}</span>
        <span>{window.to}</span>
      </div>
      <ExpandableSection
        className="pf-v6-u-mt-sm"
        toggleText={showTable ? 'Hide the daily figures' : 'Show the daily figures as a table'}
        onToggle={(_event, expanded) => setShowTable(expanded)}
        isExpanded={showTable}
      >
        <Table aria-label={`Sold out per UTC day — ${what}`} variant="compact">
          <Thead>
            <Tr>
              <Th>UTC day</Th>
              <Th>Samples</Th>
              <Th>Sold out</Th>
              <Th>Share sold out</Th>
            </Tr>
          </Thead>
          <Tbody>
            {days.map((day) => {
              const share = tallyShare(day);
              return (
                <Tr key={day.date}>
                  <Td dataLabel="UTC day">{day.date}</Td>
                  <Td dataLabel="Samples">
                    {day.sampled} of {day.expected}
                  </Td>
                  <Td dataLabel="Sold out">{day.soldOut}</Td>
                  <Td dataLabel="Share sold out">{share === null ? '—' : formatShare(share)}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </ExpandableSection>
    </>
  );
};

/**
 * Admin › Verda › Availability history — one instance type in detail (`GET /admin/verda/availability-history/
 * shape`): when it was sold out (a timeline strip per location and for any location), at which UTC hours and
 * weekdays (a heatmap), and per UTC day (columns + a table). Everything is derived from the per-slot series;
 * gaps are textured (no data), never drawn as "sold out".
 */
const VerdaAvailabilityShapeDetail: React.FunctionComponent<VerdaAvailabilityShapeDetailProps> = ({
  instanceType,
  query,
  market,
  isStaff,
  onClose,
}) => {
  const { shape, loading, error } = useAdminVerdaAvailabilityShape(instanceType, query, isStaff);
  const [location, setLocation] = useState<string>(ANYWHERE);

  const locationSeries =
    shape === null
      ? null
      : location === ANYWHERE
        ? shape.anywhere
        : (shape.locations.find((l) => l.location_code === location) ?? shape.anywhere);
  const locationLabel =
    location === ANYWHERE || shape?.locations.every((l) => l.location_code !== location) ? 'any location' : location;
  const what = `${instanceType}, ${MARKET_LABELS[market].toLowerCase()}, ${locationLabel}`;

  const renderBody = () => {
    if (error) {
      return <VerdaErrorAlert what="availability of this instance type" message={error} />;
    }
    if (shape === null || locationSeries === null) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label={`Loading the availability of ${instanceType}`} />
        </Bullseye>
      );
    }
    if (shape.locations.length === 0) {
      return <Content component="p">No sample of this instance type in the window.</Content>;
    }
    const series = locationSeries[market];
    return (
      <div style={{ opacity: loading ? 0.5 : 1 }}>
        <ScaleLegend />
        <Content component="h3" className="pf-v6-u-mt-md">
          Timeline
        </Content>
        <Timeline shape={shape} market={market} />

        <Content component="h3" className="pf-v6-u-mt-lg">
          By UTC hour and weekday
        </Content>
        <ToggleGroup isCompact aria-label="Location shown in the hourly and daily views" className="pf-v6-u-mb-sm">
          <ToggleGroupItem
            text="Any location"
            isSelected={location === ANYWHERE}
            onChange={() => setLocation(ANYWHERE)}
          />
          {shape.locations.map((l) => (
            <ToggleGroupItem
              key={l.location_code}
              text={neverAvailable(l[market]) ? `${l.location_code} (never deployable)` : l.location_code}
              isSelected={location === l.location_code}
              onChange={() => setLocation(l.location_code)}
            />
          ))}
        </ToggleGroup>
        {neverAvailable(series) ? (
          <Content component="p">
            Never deployable {MARKET_LABELS[market].toLowerCase()} in {locationLabel} in this window — not offered
            there, or sold out throughout; the data cannot tell them apart.
          </Content>
        ) : (
          <>
            <Heatmap series={series} window={shape.window} caption={`Share of samples sold out — ${what}`} />
            <Content component="h3" className="pf-v6-u-mt-lg">
              Per UTC day
            </Content>
            <Daily series={series} window={shape.window} what={what} />
          </>
        )}
      </div>
    );
  };

  const specs = shape?.instance_type;
  return (
    <Card className="pf-v6-u-mt-lg">
      <CardHeader
        actions={{
          actions: (
            <Button
              variant="plain"
              aria-label="Close the instance-type detail"
              icon={<TimesIcon />}
              onClick={onClose}
            />
          ),
        }}
      >
        <CardTitle>
          {specs ? shapeLabel(specs) : instanceType} <span className={SUBTLE}>{instanceType}</span>
        </CardTitle>
        {specs && (
          <span className={SUBTLE}>
            {[
              specs.gpu_description,
              specs.on_demand_per_hour !== null
                ? `on-demand ${formatCurrencyAmount(specs.on_demand_per_hour, 'EUR')}/h`
                : null,
              specs.spot_per_hour !== null ? `spot ${formatCurrencyAmount(specs.spot_per_hour, 'EUR')}/h` : null,
              `${MARKET_LABELS[market]} shown`,
              'hours in UTC (Brussels = UTC+1 in winter, UTC+2 in summer)',
            ]
              .filter((part) => part !== null)
              .join(' · ')}
          </span>
        )}
      </CardHeader>
      <CardBody>{renderBody()}</CardBody>
    </Card>
  );
};

export default VerdaAvailabilityShapeDetail;
